import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { isAuthenticated, getAuthConfig, getAuthToken } from '../utils/auth';
import './AppNav.css';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

function formatRelativeTime(isoString) {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function AppNav({ onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const authed = isAuthenticated();
  const isSubsActive = location.pathname === '/subscriptions';
  const isAppsActive = location.pathname === '/apps';

  // Fetch active announcements
  useEffect(() => {
    axios.get(`${API_URL}/api/announcements`)
      .then(r => setAnnouncements(r.data.announcements || []))
      .catch(() => {});
  }, []);

  const dismissAnn = (id) => {
    const next = [...dismissedAnns, id];
    setDismissedAnns(next);
    try { localStorage.setItem('oh_dismissed_anns', JSON.stringify(next)); } catch {}
  };
  const isAffiliateActive = location.pathname === '/affiliate';
  const [credit, setCredit] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);

  // Arrival toast
  const [arrivingNotif, setArrivingNotif] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnns, setDismissedAnns] = useState(() => { try { return JSON.parse(localStorage.getItem('oh_dismissed_anns') || '[]'); } catch { return []; } });
  const [annIdx, setAnnIdx] = useState(0);
  const [annPaused, setAnnPaused] = useState(false);
  const annAutoRef = useRef(null);
  const toastTimerRef = useRef(null);
  // Track whether OS push is active — if so, skip SSE toast to avoid double notifications
  const pushActiveRef = useRef(false);
  // 'granted' | 'prompt' (show Enable button) | 'denied' | 'ios-install' | 'unsupported'
  const [pushState, setPushState] = useState('unknown');

  const _urlB64ToUint8Array = (b64) => {
    const pad = '='.repeat((4 - b64.length % 4) % 4);
    const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
  };

  // Create/refresh the push subscription and register it server-side.
  const subscribeToPush = useCallback(async () => {
    await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
    const reg = await navigator.serviceWorker.ready;
    if (!reg || !reg.pushManager) throw new Error('Push not available in this browser');
    const keyRes = await fetch(`${API_URL}/api/push/vapid-public-key`);
    if (!keyRes.ok) throw new Error('VAPID key fetch failed (' + keyRes.status + ')');
    const { publicKey } = await keyRes.json();
    if (!publicKey) throw new Error('No VAPID public key configured on the server');
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: _urlB64ToUint8Array(publicKey) });
    const res = await axios.post(`${API_URL}/api/push/subscribe`, { ...sub.toJSON(), origin: window.location.origin }, getAuthConfig());
    if (!res.data?.ok) throw new Error('Server did not accept the subscription');
    pushActiveRef.current = true;
    return true;
  }, []);

  // Called from a user gesture. Safari (macOS + iOS) require
  // Notification.requestPermission() to run inside a click/tap — requesting it on
  // page load (the previous behaviour) is silently rejected there, which is why
  // OS notifications only worked on desktop Chrome.
  const enablePush = useCallback(async () => {
    try {
      if (!('Notification' in window)) { alert('This browser does not support notifications.'); return; }
      let perm = Notification.permission;
      if (perm === 'default') perm = await Notification.requestPermission();
      if (perm === 'denied') { setPushState('denied'); alert('Notifications are blocked for this site. Allow them in your browser’s site settings, then reload.'); return; }
      if (perm !== 'granted') { setPushState('prompt'); return; }
      await subscribeToPush();
      setPushState('granted');
      alert('Notifications enabled ✓');
    } catch (e) {
      setPushState('prompt');
      alert('Could not enable notifications: ' + (e && e.message ? e.message : String(e)));
    }
  }, [subscribeToPush]);

  // On load: never prompt. Silently (re)subscribe only if permission is already
  // granted; otherwise surface the correct control.
  useEffect(() => {
    if (!authed) return;
    const ua = navigator.userAgent || '';
    const isIOS = /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      // iOS Safari exposes PushManager only inside an installed (home-screen) PWA.
      setPushState(isIOS && !isStandalone ? 'ios-install' : 'unsupported');
      return;
    }
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch(() => {});
    if (Notification.permission === 'granted') {
      subscribeToPush().then(() => setPushState('granted')).catch(() => setPushState('prompt'));
    } else if (Notification.permission === 'denied') {
      setPushState('denied');
    } else {
      setPushState('prompt');
    }
  }, [authed, subscribeToPush]);

  const showToast = useCallback((data) => {
    // Skip SSE toast when OS push is active (non-incognito) to avoid double notifications
    if (pushActiveRef.current) return;
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setArrivingNotif(data);
    toastTimerRef.current = setTimeout(() => setArrivingNotif(null), 6000);
  }, []);

  useEffect(() => {
    if (!authed) return;
    axios.get(`${API_URL}/api/subscriptions/account-balance`, getAuthConfig())
      .then(res => {
        const bal = res.data?.balanceCents;
        if (typeof bal === 'number' && bal < 0) setCredit(Math.abs(bal));
        else setCredit(0);
      })
      .catch(() => {});
  }, [authed, location.pathname]);

  // Load notifications on mount
  useEffect(() => {
    if (!authed) return;
    axios.get(`${API_URL}/api/notifications`, getAuthConfig())
      .then(res => {
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unreadCount || 0);
      })
      .catch(() => {});
  }, [authed]);

  // SSE connection for live notifications
  useEffect(() => {
    if (!authed) return;
    const token = getAuthToken();
    if (!token) return;
    const es = new EventSource(`${API_URL}/api/notifications/stream?token=${encodeURIComponent(token)}`);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.event === 'init') {
          setUnreadCount(data.unread || 0);
          return;
        }
        // Ephemeral events (typing, read receipts, status) — pass through to ticket views only, no notification
        if (data.event === 'typing' || data.event === 'read' || data.event === 'support_status') {
          window.dispatchEvent(new CustomEvent('openhosst:notification', { detail: data }));
          return;
        }
        // Update bell counter and show in-app toast (works in incognito + when page is open)
        // OS push notification handles background/closed tab delivery
        setNotifications(prev => [data, ...prev].slice(0, 50));
        setUnreadCount(n => n + 1);
        showToast(data);
        // Play notification sound
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.connect(g); g.connect(ctx.destination);
          o.frequency.setValueAtTime(880, ctx.currentTime);
          o.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
          g.gain.setValueAtTime(0.15, ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
          o.start(ctx.currentTime);
          o.stop(ctx.currentTime + 0.35);
        } catch {}
        // Broadcast so open ticket views can refresh immediately
        window.dispatchEvent(new CustomEvent('openhosst:notification', { detail: data }));
      } catch {}
    };
    es.onerror = () => {};
    // Dispatch reload event on RECONNECT only (not the initial connection)
    let _sseOpened = false;
    es.onopen = () => {
      if (_sseOpened) window.dispatchEvent(new CustomEvent('openhosst:sse-reconnect'));
      _sseOpened = true;
    };
    return () => es.close();
  }, [authed, showToast]);

  // Close-outside handler for notification dropdown
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const goToTicket = (notif) => {
    const ticketId = notif.extra?.ticketId || notif.link;
    const url = ticketId ? `/subscriptions?tab=support&ticket=${ticketId}` : '/subscriptions?tab=support';
    navigate(url);
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      axios.post(`${API_URL}/api/notifications/${notif.id}/read`, {}, getAuthConfig()).catch(() => {});
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
      setUnreadCount(n => Math.max(0, n - 1));
    }
    setNotifOpen(false);
    goToTicket(notif);
  };

  const handleMarkAllRead = async () => {
    axios.post(`${API_URL}/api/notifications/read-all`, {}, getAuthConfig()).catch(() => {});
    setNotifications([]);
    setUnreadCount(0);
  };

  const visibleAnns = announcements.filter(a => !dismissedAnns.includes(a.id));
  const annColors = { info: { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', icon: 'fa-circle-info' }, warning: { bg: '#fffbeb', border: '#fde68a', text: '#92400e', icon: 'fa-triangle-exclamation' }, success: { bg: '#f0fdf4', border: '#bbf7d0', text: '#14532d', icon: 'fa-circle-check' }, danger: { bg: '#fef2f2', border: '#fecaca', text: '#7f1d1d', icon: 'fa-circle-exclamation' } };
  const safeAnnIdx = visibleAnns.length > 0 ? Math.min(annIdx, visibleAnns.length - 1) : 0;
  const currentAnn = visibleAnns[safeAnnIdx] || null;

  // Auto-slide announcements every 5 s (pause on hover / manual nav)
  useEffect(() => {
    if (annAutoRef.current) clearInterval(annAutoRef.current);
    if (visibleAnns.length <= 1 || annPaused) return;
    annAutoRef.current = setInterval(() => {
      setAnnIdx(i => (i + 1) % visibleAnns.length);
    }, 5000);
    return () => clearInterval(annAutoRef.current);
  }, [visibleAnns.length, annPaused]);

  const handleDismissAnn = (id) => {
    dismissAnn(id);
    // Move to next if dismissing current, or clamp
    setAnnIdx(prev => Math.max(0, Math.min(prev, visibleAnns.length - 2)));
  };

  return (
    <>
      {currentAnn && (() => {
        const ann = currentAnn;
        const c = annColors[ann.type] || annColors.info;
        const total = visibleAnns.length;
        return (
          <div style={{ background: c.bg, borderBottom: `1px solid ${c.border}`, fontSize: '13px', color: c.text, lineHeight: '1.5', zIndex: 101, position: 'relative', overflow: 'hidden', minHeight: '44px' }}
            onMouseEnter={() => setAnnPaused(true)} onMouseLeave={() => setAnnPaused(false)}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', gap: '8px', minHeight: '44px', boxSizing: 'border-box' }}>
              {/* Prev arrow */}
              {total > 1 && (
                <button onClick={() => { setAnnPaused(true); setTimeout(() => setAnnPaused(false), 8000); setAnnIdx(i => (i - 1 + total) % total); }}
                  style={{ background: 'none', border: 'none', color: c.text, opacity: 0.5, cursor: 'pointer', fontSize: '14px', padding: '0 4px', flexShrink: 0 }} aria-label="Previous announcement">
                  <i className="fa-solid fa-chevron-left" />
                </button>
              )}
              {/* Content */}
              <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', maxWidth: '100%', justifyContent: 'center' }}>
                  <i className={`fa-solid ${c.icon}`} style={{ flexShrink: 0 }} />
                  <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: '700' }}>{ann.title}</span>
                    {ann.body && <span style={{ opacity: 0.9 }}>{' — '}{ann.body}</span>}
                  </div>
                  <a
                    href={ann.cta_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => { if (!ann.cta_url) e.preventDefault(); else e.stopPropagation(); }}
                    style={{ display: 'inline-block', padding: '3px 12px', borderRadius: '20px', border: `1px solid ${c.text}`, color: c.text, fontSize: '12px', fontWeight: '700', textDecoration: 'none', whiteSpace: 'nowrap', opacity: 0.85, flexShrink: 0, visibility: ann.cta_label && ann.cta_url ? 'visible' : 'hidden' }}
                  >
                    {ann.cta_label || ' '} <i className="fa-solid fa-arrow-right" style={{ fontSize: '10px', marginLeft: '3px' }} />
                  </a>
                </div>
              </div>
              {/* Counter + dots */}
              {total > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                  {visibleAnns.map((_, i) => (
                    <button key={i} onClick={() => { setAnnPaused(true); setTimeout(() => setAnnPaused(false), 8000); setAnnIdx(i); }} aria-label={`Announcement ${i + 1}`}
                      style={{ width: i === safeAnnIdx ? '16px' : '6px', height: '6px', borderRadius: '3px', background: c.text, opacity: i === safeAnnIdx ? 0.8 : 0.25, border: 'none', padding: 0, cursor: 'pointer', transition: 'width 0.25s, opacity 0.25s' }} />
                  ))}
                </div>
              )}
              {/* Next arrow */}
              {total > 1 && (
                <button onClick={() => { setAnnPaused(true); setTimeout(() => setAnnPaused(false), 8000); setAnnIdx(i => (i + 1) % total); }}
                  style={{ background: 'none', border: 'none', color: c.text, opacity: 0.5, cursor: 'pointer', fontSize: '14px', padding: '0 4px', flexShrink: 0 }} aria-label="Next announcement">
                  <i className="fa-solid fa-chevron-right" />
                </button>
              )}
              {/* Dismiss */}
              {ann.dismissable && (
                <button onClick={() => handleDismissAnn(ann.id)}
                  style={{ background: 'none', border: 'none', color: c.text, opacity: 0.5, cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '0 4px', flexShrink: 0 }} aria-label="Dismiss">×</button>
              )}
            </div>
          </div>
        );
      })()}
      <nav className="app-nav">
      <a href="https://openhosst.com" className="app-nav-logo">
        <img src="/logo-dark.png" alt="OpenHosst" />
      </a>

      <button className="app-nav-hamburger" onClick={() => setMenuOpen(v => !v)} aria-label="Toggle menu">
        <i className={`fa-solid ${menuOpen ? 'fa-times' : 'fa-bars'}`} />
      </button>

      {/* Enable OS notifications — must be triggered by a tap (Safari/iOS requirement) */}
      {authed && pushState === 'prompt' && (
        <button className="nav-notif-btn" onClick={enablePush} title="Enable notifications" aria-label="Enable notifications">
          <i className="fa-solid fa-bell-slash" />
        </button>
      )}
      {authed && pushState === 'ios-install' && (
        <button
          className="nav-notif-btn"
          onClick={() => alert('To get notifications on iPhone/iPad: open OpenHosst in Safari, tap the Share button, choose “Add to Home Screen”, then open the app from that new icon and tap the bell to enable notifications.')}
          title="Add to Home Screen for notifications"
          aria-label="Add to Home Screen for notifications"
        >
          <i className="fa-solid fa-bell-slash" />
        </button>
      )}

      {/* Notification bell — always in the top bar on both desktop and mobile */}
      {authed && (
        <div className="nav-notif-wrapper nav-notif-wrapper--bar" ref={notifRef}>
          <button
            className="nav-notif-btn"
            onClick={() => setNotifOpen(v => !v)}
            aria-label="Notifications"
          >
            <i className="fa-solid fa-bell" />
            {unreadCount > 0 && (
              <span className="nav-notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>
          {notifOpen && (
            <div className="nav-notif-dropdown">
              <div className="nav-notif-header">
                <span>Notifications</span>
                <button onClick={handleMarkAllRead} disabled={unreadCount === 0} style={{ opacity: unreadCount === 0 ? 0.4 : 1 }}>Mark all read</button>
              </div>
              <div className="nav-notif-list">
                {notifications.filter(n => !n.read).length === 0 ? (
                  <div className="nav-notif-empty">
                    <i className="fa-solid fa-bell-slash" style={{ marginRight: 8 }} />
                    No new notifications
                  </div>
                ) : notifications.filter(n => !n.read).map(n => (
                  <div
                    key={n.id}
                    className="nav-notif-item unread"
                    onClick={() => handleNotifClick(n)}
                  >
                    <div className="nav-notif-item-title">{n.title}</div>
                    <div className="nav-notif-item-msg">{n.message}</div>
                    <div className="nav-notif-item-time">{formatRelativeTime(n.created_at)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Collapsible nav menu */}
      <div className={`app-nav-right${menuOpen ? ' open' : ''}`}>
        <Link
          to="/apps"
          className={`nav-apps-btn${isAppsActive ? ' active' : ''}`}
          onClick={closeMenu}
        >
          App Store
        </Link>
        {authed ? (
          <>
            <Link
              to="/subscriptions"
              className={`nav-my-subs-btn${isSubsActive ? ' active' : ''}`}
              onClick={closeMenu}
            >
              My Subscriptions
            </Link>
            <Link
              to="/affiliate"
              className={`nav-affiliate-btn${isAffiliateActive ? ' active' : ''}`}
              onClick={closeMenu}
            >
              Earn
            </Link>
            <button className="nav-logout-btn" onClick={() => { closeMenu(); onLogout(); }}>
              Logout
            </button>
            {credit > 0 && (
              <span className="nav-credit-badge">
                <i className="fa-solid fa-wallet"></i>
                ${(credit / 100).toFixed(2)}
              </span>
            )}
          </>
        ) : (
          <>
            <Link to="/login" className="nav-login-btn nav-login-ghost" onClick={closeMenu}>
              Sign in
            </Link>
            <Link to="/signup" className="nav-login-btn" onClick={closeMenu}>
              Get Started
            </Link>
          </>
        )}
      </div>

      {/* Arrival toast */}
      {arrivingNotif && (() => {
        const ex = arrivingNotif.extra || {};
        const ticketTypes = ['ticket_reply','ticket_admin_reply','ticket_new','ticket_admin_created','ticket_status'];
        const isTicket = ticketTypes.includes(arrivingNotif.type);
        const metaParts = [];
        if (ex.ticketId || arrivingNotif.link) metaParts.push(`Ticket #${ex.ticketId || arrivingNotif.link}`);
        if (ex.domain) metaParts.push(ex.domain);
        if (ex.userEmail && ex.userEmail !== ex.senderName) metaParts.push(ex.userEmail);
        return (
          <div className="nav-notif-toast" onClick={() => { goToTicket(arrivingNotif); setArrivingNotif(null); }}>
            <div className="nav-notif-toast-header">
              <div className="nav-notif-toast-site">
                <img src="/openhosst-favicon.png" alt="" className="nav-notif-toast-favicon" onError={e => e.target.style.display='none'} />
                <span>OpenHosst</span>
              </div>
              <button className="nav-notif-toast-close" onClick={e => { e.stopPropagation(); setArrivingNotif(null); }}>
                <i className="fa-solid fa-times" />
              </button>
            </div>
            <div className="nav-notif-toast-content">
              <div className="nav-notif-toast-icon">
                <i className={`fa-solid ${isTicket ? 'fa-headset' : 'fa-bell'}`} />
              </div>
              <div className="nav-notif-toast-body">
                <div className="nav-notif-toast-title">{arrivingNotif.title}</div>
                {ex.senderName && <div className="nav-notif-toast-from">From: {ex.senderName}</div>}
                {metaParts.length > 0 && <div className="nav-notif-toast-meta">{metaParts.join(' · ')}</div>}
                {arrivingNotif.message && <div className="nav-notif-toast-preview">"{arrivingNotif.message}"</div>}
                {!isTicket && !ex.senderName && <div className="nav-notif-toast-msg">{arrivingNotif.message}</div>}
              </div>
            </div>
          </div>
        );
      })()}
    </nav>
    </>
  );
}

export default AppNav;
